import { useEffect, useMemo, useRef, useState } from 'react';
import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import { asId, type ProjectId, type WallId } from '@obraplan/core/domain/shared/ids';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import {
  checkElementTypeFields,
  ELEMENT_TYPE_NAME_MAX,
  type StructuralElementType,
} from '@obraplan/core/domain/takeoff/element-type';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  STRUCTURAL_ELEMENT_KIND_LABEL,
  type ElementSegment,
  type StructuralElement,
  type StructuralElementKind,
} from '@obraplan/core/domain/takeoff/structural-element';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import {
  acoAprovadoDoElemento,
  fatoresDoElemento,
  mediaDoAcoDaPeca,
  taxaDeBaseDaPeca,
} from './fatores-proprios';
import { AMediaDoTipoMudou, FatoresProprios } from './FatoresProprios';
import { AcoDaPeca, taxaPropria, type FalaDaPeca } from './FundacaoNaPrancha';
import {
  atualizarTipoPelaMedia,
  mediaDoTipoNovo,
  mudancasDoTipo,
  semNadaNoTipo,
} from './media-no-tipo';
import { aprovacaoDe, type ServicosAprovados } from './servicos-aprovados';
import type { WorkspaceAction } from './typology-draft';

/**
 * O tipo, o fator próprio e o aço do pilar e da viga na planta (Fase 42, etapas 1 e 2 — G1, G2,
 * G5, G6, G7; RN-561 a RN-563, RN-567 a RN-570).
 *
 * Sem tela nem modal novo (pedido do Gilvan, 27/10/2026): mora na linha do pilar ou da viga da
 * lista "Vãos e peças" da prancha, e abre quando a peça está selecionada — clicada no desenho ou
 * na lista. É o molde do card do bloco (Fase 41): o campo "Tipo" escolhe um tipo da obra ou faz
 * nascer um pelo nome; as linhas de fator mostram o do tipo como base, o digitado é o próprio da
 * peça, e "Aplicar ao tipo P20" o leva para a composição; "Aplicar o P20 a outros pilares",
 * recolhido, dá o tipo a vários de uma vez. O recolhível "Aço deste pilar (kg/m³)" (etapa 2) mostra
 * a taxa que vale — a própria, a do tipo ou a da disciplina — e de onde vem; com um serviço de aço
 * do elemento aprovado, só a mostra (RN-570).
 *
 * Pilar e viga usam o mesmo card, cada um com os tipos dele (RN-257): o campo só oferece os tipos
 * do mesmo elemento, e o nome repetido só conta dentro do elemento — "P1" pode ser de pilar e de
 * viga.
 *
 * Com a média da estrutura (etapa 4 — G10, RN-582 a RN-584): o tipo novo nasce com ela — o fator
 * médio de cada item dos serviços do elemento e a taxa média do aço dele, "da média" —; a linha do
 * tipo que a segue e mudou mostra "A média mudou", com o "Atualizar"; e cada linha de fator e cada
 * bitola mostram a média de hoje ao lado, como o bloco (RN-554).
 */

/** Como a tela fala de cada elemento: o pilar e a viga não têm o mesmo gênero. */
const FALA: Readonly<
  Record<
    StructuralElementKind,
    FalaDaPeca & {
      readonly plural: string;
      readonly o: string;
      readonly outros: string;
      readonly todo: string;
    }
  >
> = {
  PILAR: {
    nome: 'pilar',
    plural: 'pilares',
    o: 'o',
    do: 'do',
    deste: 'deste',
    neste: 'neste',
    outros: 'outros',
    todo: 'todo',
  },
  VIGA: {
    nome: 'viga',
    plural: 'vigas',
    o: 'a',
    do: 'da',
    deste: 'desta',
    neste: 'nesta',
    outros: 'outras',
    todo: 'toda',
  },
};

/** Centímetros inteiros — a seção é como a obra fala: "20 × 40". */
const cm = (metros: number): string => String(Math.round(metros * 100));

/**
 * O nome de cada pilar ou viga na lista do "Aplicar a outros": o nome dele, ou o elemento com a
 * seção, e a parede em que ele está — sem nome, "Pilar 20 × 40 cm" se repete pela planta, e a
 * parede é o que a pessoa reconhece. O que ainda se repetir ganha o número da vez.
 */
export function rotulosDosElementos(
  elementos: readonly StructuralElement[],
  trechos: readonly ElementSegment[],
  nomeDaParede: (wallId: WallId) => string,
): ReadonlyMap<string, string> {
  const primeiraParede = new Map<string, WallId>();
  for (const trecho of trechos) {
    if (!primeiraParede.has(trecho.elementId)) primeiraParede.set(trecho.elementId, trecho.wallId);
  }
  const brutos = elementos.map((element): [string, string] => {
    const parede = primeiraParede.get(element.id);
    const nome = `${element.name ?? STRUCTURAL_ELEMENT_KIND_LABEL[element.kind]} ${cm(
      element.sectionWidth,
    )} × ${cm(element.sectionDepth)} cm`;
    return [
      element.id,
      parede === undefined ? `${nome} · solto` : `${nome} · ${nomeDaParede(parede)}`,
    ];
  });
  const vezes = new Map<string, number>();
  for (const [, rotulo] of brutos) vezes.set(rotulo, (vezes.get(rotulo) ?? 0) + 1);
  const contagem = new Map<string, number>();
  return new Map(
    brutos.map(([id, rotulo]) => {
      if ((vezes.get(rotulo) ?? 0) < 2) return [id, rotulo];
      const vez = (contagem.get(rotulo) ?? 0) + 1;
      contagem.set(rotulo, vez);
      return [id, `${rotulo} (${String(vez)})`];
    }),
  );
}

/**
 * O tipo e os fatores do pilar ou da viga selecionado, dentro da linha dele na lista da prancha.
 */
export function TipoEFatoresDoElemento({
  elemento,
  elementos,
  trechos,
  nomeDaParede,
  tipos,
  projectId,
  catalogo,
  aprovados,
  media,
  erro,
  dispatch: despachar,
}: {
  elemento: StructuralElement;
  /** Os pilares e vigas do desenho — a lista do "Aplicar o P20 a outros pilares" (RN-562). */
  elementos: readonly StructuralElement[];
  /** Os trechos deles, para a lista dizer em que parede cada um está. */
  trechos: readonly ElementSegment[];
  nomeDaParede: (wallId: WallId) => string;
  /** Os tipos de pilar e de viga da obra (RN-561) — o card mostra só os do elemento. */
  tipos: readonly StructuralElementType[];
  /** A obra do tipo que nasce no campo. Ausente, a dos tipos que já existem. */
  projectId?: ProjectId;
  /** O cadastro da obra, para as linhas de fator e o aço (RN-563, RN-568). Ausente, não aparecem. */
  catalogo?: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /**
   * Os serviços aprovados no orçamento (Fase 33, RN-438): o de aço que mede o elemento trava a
   * taxa que a peça lê (RN-570). Ausente, nada trava na tela — o servidor segura o que passar.
   */
  aprovados?: ServicosAprovados | undefined;
  /**
   * A média das obras marcadas (`useMediaDoApartamento`): a da estrutura ao lado do fator e de
   * cada bitola, e o tipo novo nasce com ela (etapa 4 — RN-582, RN-584). Ausente, o card é o de
   * antes.
   */
  media?: ApartmentAverageForProject | undefined;
  /** A última recusa — só aparece depois de a pessoa mexer **neste** card. */
  erro: string | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const [editou, setEditou] = useState(false);
  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const fala = FALA[elemento.kind];
  const doElemento = useMemo(
    () => tipos.filter((tipo) => tipo.kind === elemento.kind && tipo.deletedAt === undefined),
    [tipos, elemento.kind],
  );
  const tipo = doElemento.find((candidato) => candidato.id === elemento.elementTypeId);
  const fatores = useMemo(
    () => fatoresDoElemento(catalogo, elemento, tipo, media),
    [catalogo, elemento, tipo, media],
  );
  const mediaDoAco = useMemo(() => mediaDoAcoDaPeca(media, elemento.kind), [media, elemento.kind]);
  const travado = (serviceKey: string): boolean =>
    aprovados !== undefined && aprovacaoDe(aprovados, serviceKey) !== undefined;
  // A linha do tipo que segue a média e mudou (RN-583).
  const mudancas =
    tipo === undefined ? undefined : mudancasDoTipo(catalogo, elemento.kind, tipo, media, travado);
  const rotulos = useMemo(
    () => rotulosDosElementos(elementos, trechos, nomeDaParede),
    [elementos, trechos, nomeDaParede],
  );
  // A peça clicada no desenho abre o card lá embaixo, na lista: ele vem para a vista.
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    raiz.current?.scrollIntoView?.({ block: 'nearest' });
  }, [elemento.id]);

  return (
    <div
      ref={raiz}
      role="group"
      aria-label={`Tipo e fatores ${fala.deste} ${fala.nome}`}
      className="flex flex-col gap-2 border-t border-(--color-border-subtle) pt-2 pb-2"
    >
      <CampoDoTipo
        elemento={elemento}
        tipos={doElemento}
        todosOsTipos={tipos}
        projectId={projectId}
        catalogo={catalogo}
        media={media}
        travado={travado}
        dispatch={dispatch}
      />
      {tipo !== undefined && mudancas !== undefined && (
        <AMediaDoTipoMudou
          tipo={tipo.name}
          paraQuem={`${fala.todo} ${fala.nome} dele`}
          mudancas={mudancas}
          onAtualizar={() =>
            dispatch({
              type: 'APPLY_ELEMENT_TYPE_AVERAGE',
              typeId: tipo.id,
              ...atualizarTipoPelaMedia(catalogo, elemento.kind, tipo, media, travado),
            })
          }
        />
      )}
      {tipo !== undefined && (
        <AplicarOTipoAOutros
          elemento={elemento}
          tipo={tipo}
          elementos={elementos}
          rotulos={rotulos}
          dispatch={dispatch}
        />
      )}
      {catalogo !== undefined && (
        <FatoresProprios
          idBase={`elemento-${elemento.id}-fator`}
          titulo={`Fatores ${fala.deste} ${fala.nome}`}
          servicos={fatores}
          rotuloDaVolta={tipo === undefined ? 'Voltar ao índice' : 'Voltar ao do tipo'}
          vazio={`Nenhum serviço da obra mede ${fala.o} ${fala.nome} com item a converter.`}
          onMudar={(serviceKey, itemKey, factor) =>
            dispatch({
              type: 'SET_ELEMENT_FACTOR',
              elementId: elemento.id,
              serviceKey,
              itemKey,
              ...(factor === undefined ? {} : { factor }),
            })
          }
          // O próprio desta peça vira a composição do tipo, e a peça volta a lê-la (RN-561).
          aplicarAoTipo={
            tipo === undefined
              ? undefined
              : {
                  nome: tipo.name,
                  onAplicar: (serviceKey, itemKey, factor) => {
                    dispatch({
                      type: 'SET_ELEMENT_TYPE_RATE',
                      typeId: tipo.id,
                      serviceKey,
                      itemKey,
                      factor,
                    });
                    dispatch({
                      type: 'SET_ELEMENT_FACTOR',
                      elementId: elemento.id,
                      serviceKey,
                      itemKey,
                    });
                  },
                }
          }
        />
      )}
      {catalogo !== undefined && (
        <AcoDaPeca
          propria={taxaPropria(elemento.steelRates)}
          fala={fala}
          base={taxaDeBaseDaPeca(catalogo, elemento.kind, tipo)}
          media={mediaDoAco}
          onMudar={(rates) =>
            dispatch({ type: 'SET_ELEMENT_STEEL_RATES', elementId: elemento.id, rates })
          }
          // A própria desta peça vira a taxa do tipo, e a peça volta a lê-la de lá (RN-567).
          aplicarAoTipo={
            tipo === undefined
              ? undefined
              : {
                  nome: tipo.name,
                  onAplicar: (taxas) => {
                    dispatch({
                      type: 'SET_ELEMENT_TYPE_STEEL_RATES',
                      typeId: tipo.id,
                      rates: taxas,
                    });
                    dispatch({
                      type: 'SET_ELEMENT_STEEL_RATES',
                      elementId: elemento.id,
                      rates: [],
                    });
                  },
                }
          }
          travadaPor={acoAprovadoDoElemento(aprovados, catalogo, elemento.kind)}
        />
      )}
      {editou && erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}
    </div>
  );
}

/**
 * O campo "Tipo" do pilar ou da viga (RN-561, RN-562): escolhe um tipo do elemento na lista, ou
 * digita um nome novo — e o tipo nasce ali (`ADD_ELEMENT_TYPE`), já dado a esta peça. Vazio tira
 * o tipo: a peça volta a seguir a obra. Grava ao sair do campo ou no Enter.
 *
 * Renomear e excluir o tipo ficam fora da tela, como no bloco: no mesmo campo, digitar outro nome
 * já quer dizer "trocar de tipo ou criar um". As ações existem na bancada.
 *
 * O tipo novo nasce com a média da estrutura (etapa 4 — G10, RN-582): `mediaDoTipoNovo`. Sem
 * média, nasce vazio, como antes.
 */
function CampoDoTipo({
  elemento,
  tipos,
  todosOsTipos,
  projectId,
  catalogo,
  media,
  travado,
  dispatch,
}: {
  elemento: StructuralElement;
  /** Os tipos vivos do mesmo elemento. */
  tipos: readonly StructuralElementType[];
  /** Todos os da obra, para a conferência do nome do tipo novo. */
  todosOsTipos: readonly StructuralElementType[];
  projectId: ProjectId | undefined;
  catalogo: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /** A média das obras marcadas: o tipo novo nasce com a da estrutura (RN-582). */
  media: ApartmentAverageForProject | undefined;
  /** O serviço aprovado: o tipo novo não ganha linha dele (RN-566, RN-570). */
  travado: (serviceKey: string) => boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const fala = FALA[elemento.kind];
  const tipo = tipos.find((candidato) => candidato.id === elemento.elementTypeId);
  const [texto, setTexto] = useState(tipo?.name ?? '');
  const [erro, setErro] = useState<string | undefined>(undefined);
  const id = `elemento-${elemento.id}-tipo`;

  useEffect(() => {
    setTexto(tipo?.name ?? '');
  }, [tipo?.name]);

  const aplicar = (): void => {
    const nome = texto.trim();
    setErro(undefined);
    if (nome === '') {
      if (elemento.elementTypeId !== undefined) {
        dispatch({ type: 'SET_ELEMENT_TYPE_OF', elementIds: [elemento.id] });
      }
      return;
    }
    const existente = tipos.find(
      (candidato) => normalizeName(candidato.name) === normalizeName(nome),
    );
    if (existente !== undefined) {
      setTexto(existente.name);
      if (existente.id !== elemento.elementTypeId) {
        dispatch({ type: 'SET_ELEMENT_TYPE_OF', elementIds: [elemento.id], typeId: existente.id });
      }
      return;
    }
    // O tipo novo: a conferência do domínio antes, para a recusa ficar no campo e o nome digitado
    // não se perder; depois o tipo nasce e já vai para a peça.
    const conferido = checkElementTypeFields(
      { kind: elemento.kind, name: nome, rates: [] },
      todosOsTipos,
    );
    if (!conferido.ok) {
      setErro(conferido.error.message);
      return;
    }
    const novo = asId<'StructuralElementType'>(crypto.randomUUID());
    dispatch({
      type: 'ADD_ELEMENT_TYPE',
      kind: elemento.kind,
      name: conferido.value.name,
      id: novo,
      ...(projectId === undefined ? {} : { projectId }),
    });
    // O tipo nasce com a média da estrutura (RN-582), antes de ir para a peça.
    const daMedia = mediaDoTipoNovo(catalogo, elemento.kind, media, travado);
    if (!semNadaNoTipo(daMedia)) {
      dispatch({ type: 'APPLY_ELEMENT_TYPE_AVERAGE', typeId: novo, ...daMedia });
    }
    dispatch({ type: 'SET_ELEMENT_TYPE_OF', elementIds: [elemento.id], typeId: novo });
  };

  return (
    <div className="flex flex-col gap-1">
      <Field label="Tipo" htmlFor={id}>
        <Input
          id={id}
          list={`${id}-lista`}
          value={texto}
          maxLength={ELEMENT_TYPE_NAME_MAX}
          placeholder="sem tipo — segue a obra"
          className="w-48"
          aria-invalid={erro !== undefined}
          onChange={(evento) => setTexto(evento.target.value)}
          onBlur={aplicar}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter') aplicar();
          }}
        />
      </Field>
      <datalist id={`${id}-lista`}>
        {tipos.map((candidato) => (
          <option key={candidato.id} value={candidato.name} />
        ))}
      </datalist>
      <span className="text-xs text-(--color-content-muted)">
        {tipo === undefined
          ? `Escolha um tipo de ${fala.nome} da obra ou digite um nome novo. Sem tipo, ${fala.o} ${fala.nome} segue o índice da obra.`
          : `Os fatores "do tipo" abaixo são a composição do ${tipo.name}, para ${fala.todo} ${fala.nome} dele.`}
      </span>
      {erro !== undefined && (
        <span role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </span>
      )}
    </div>
  );
}

/**
 * "Aplicar o P20 a outros pilares" (RN-562): dar o tipo desta peça a várias de uma vez, dentro do
 * próprio card — a prancha não tem seleção de várias peças. Fechado por padrão. Só as peças do
 * mesmo elemento, e as que ainda não são do tipo.
 */
function AplicarOTipoAOutros({
  elemento,
  tipo,
  elementos,
  rotulos,
  dispatch,
}: {
  elemento: StructuralElement;
  tipo: StructuralElementType;
  elementos: readonly StructuralElement[];
  rotulos: ReadonlyMap<string, string>;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const fala = FALA[elemento.kind];
  const outros = elementos.filter(
    (outro) =>
      outro.id !== elemento.id &&
      outro.kind === elemento.kind &&
      outro.deletedAt === undefined &&
      outro.elementTypeId !== tipo.id,
  );
  if (outros.length === 0) return null;
  const escolhidos = outros.filter((outro) => marcados.has(outro.id));
  const plural = fala.plural.charAt(0).toLocaleUpperCase('pt-BR') + fala.plural.slice(1);

  return (
    <details className="flex flex-col gap-2 text-xs">
      <summary className="cursor-pointer text-sm font-medium">
        Aplicar o {tipo.name} a {fala.outros} {fala.plural}
      </summary>
      <ul aria-label={`${plural} sem o ${tipo.name}`} className="mt-2 flex flex-col gap-1">
        {outros.map((outro) => {
          const id = `elemento-${elemento.id}-aplicar-${outro.id}`;
          return (
            <li key={outro.id} className="flex items-center gap-1">
              <input
                id={id}
                type="checkbox"
                className="size-4"
                checked={marcados.has(outro.id)}
                onChange={(evento) => {
                  const novos = new Set(marcados);
                  if (evento.target.checked) novos.add(outro.id);
                  else novos.delete(outro.id);
                  setMarcados(novos);
                }}
              />
              <label htmlFor={id}>{rotulos.get(outro.id) ?? fala.nome}</label>
            </li>
          );
        })}
      </ul>
      <div className="mt-1 flex flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            setMarcados(
              escolhidos.length === outros.length
                ? new Set()
                : new Set(outros.map((outro) => String(outro.id))),
            )
          }
        >
          {escolhidos.length === outros.length ? 'Desmarcar todos' : 'Marcar todos'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={escolhidos.length === 0}
          onClick={() => {
            dispatch({
              type: 'SET_ELEMENT_TYPE_OF',
              elementIds: escolhidos.map((outro) => outro.id),
              typeId: tipo.id,
            });
            setMarcados(new Set());
          }}
        >
          Aplicar o {tipo.name}
          {escolhidos.length > 0 && ` a ${String(escolhidos.length)}`}
        </Button>
      </div>
    </details>
  );
}
